/**
 * Vérification d'identité — un flux par profil (patient / professionnel / établissement).
 * Voir docs/4-CLARIFICATION_RELATION_ETABLISSEMENT_PATIENT/
 *   2-MISE_EN_PLACE_VALIDATION_DE_COMPTE_PATIENT_ETABLISSEMENT_PROFESSIONNEL_IMPLEMENTED.md
 */

import { apiFetch } from "./client";

export type IdentityVerificationStatus = "non_soumis" | "en_attente" | "validee" | "rejetee";

export type PatientIdentityVerification = {
  statut: IdentityVerificationStatus;
  carteCipUrl: string | null;
  dateSoumission: string | null;
  dateTraitement: string | null;
  motifRejet: string | null;
};

export type ProfessionalIdentityVerification = {
  statut: IdentityVerificationStatus;
  carteCipUrl: string | null;
  attestationOrdreUrl: string | null;
  dateSoumission: string | null;
  dateTraitement: string | null;
  motifRejet: string | null;
};

export type EtablissementIdentityVerification = {
  statut: IdentityVerificationStatus;
  carteCipResponsableUrl: string | null;
  arreteMinisterielUrl: string | null;
  dateSoumission: string | null;
  dateTraitement: string | null;
  motifRejet: string | null;
};

type ApiResult<T> = { ok: true; data: T } | { ok: false; message: string; errorCode?: string };

function unwrap<T>(payload: unknown): T {
  const p = payload as { data?: unknown } & Record<string, unknown>;
  return (p?.data ?? p) as T;
}

// --- Patient ---------------------------------------------------------------

export async function getPatientIdentityVerification(nip: string): Promise<ApiResult<PatientIdentityVerification>> {
  const out = await apiFetch<unknown>(`/patient/patients/${encodeURIComponent(nip)}/identity-verification`);
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<PatientIdentityVerification>(out.data) };
}

export async function submitPatientIdentityVerification(
  nip: string,
  body: { carteCipUrl: string }
): Promise<ApiResult<PatientIdentityVerification>> {
  const out = await apiFetch<unknown>(`/patient/patients/${encodeURIComponent(nip)}/identity-verification`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<PatientIdentityVerification>(out.data) };
}

// --- Professionnel -----------------------------------------------------------

export async function getProfessionalIdentityVerification(
  nip: string
): Promise<ApiResult<ProfessionalIdentityVerification>> {
  const out = await apiFetch<unknown>(
    `/professionnel/professionnels/${encodeURIComponent(nip)}/identity-verification`
  );
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<ProfessionalIdentityVerification>(out.data) };
}

export async function submitProfessionalIdentityVerification(
  nip: string,
  body: { carteCipUrl?: string; attestationOrdreUrl?: string }
): Promise<ApiResult<ProfessionalIdentityVerification>> {
  const out = await apiFetch<unknown>(
    `/professionnel/professionnels/${encodeURIComponent(nip)}/identity-verification`,
    { method: "POST", body: JSON.stringify(body) }
  );
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<ProfessionalIdentityVerification>(out.data) };
}

// --- Établissement -----------------------------------------------------------

export async function getEtablissementIdentityVerification(
  id: string
): Promise<ApiResult<EtablissementIdentityVerification>> {
  const out = await apiFetch<unknown>(`/etablissement/etablissements/${encodeURIComponent(id)}/identity-verification`);
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<EtablissementIdentityVerification>(out.data) };
}

export async function submitEtablissementIdentityVerification(
  id: string,
  body: { carteCipResponsableUrl?: string; arreteMinisterielUrl?: string }
): Promise<ApiResult<EtablissementIdentityVerification>> {
  const out = await apiFetch<unknown>(
    `/etablissement/etablissements/${encodeURIComponent(id)}/identity-verification`,
    { method: "POST", body: JSON.stringify(body) }
  );
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<EtablissementIdentityVerification>(out.data) };
}
